<template>
  <div class="column gap-4">
    <div v-if="chartAxisConfig?.item.includes('target')">
      <h3 class="label mt-0">{{ $t("objectNames.target", 5) }}</h3>
      <!-- Marker for ALL target option -->
      <button-item
        v-if="dataToggles.all.target"
        class="mb-4"
        :icons="['x']"
        @click:x="toggleAllOption('target')"
      >
        {{ $t("labels.allTargets") }}
      </button-item>

      <sortable-list
        :items="targets"
        :order="selectedTargetsOrder"
        prop="title"
        :sortable="true"
        @update:order="updateItemOrder('target', $event)"
      >
        <template #default="{ item: target }">
          <chart-data-button-group
            :chart-uid="chartUid"
            item-type="target"
            :item-id="target.id"
            :subgroups="subgroups"
            @remove="removeItem(target.uid)"
          >
            {{ target.title }}
          </chart-data-button-group>
        </template>
        <template #after-list>
          <item-combo-select
            v-if="!dataToggles.all.target"
            :selection="Object.values(targets)"
            :targets="allAvailableTargets"
            :resources="{}"
            :indices="{}"
            :types="['target']"
            :placeholder="$t('actions.addTarget', 2)"
            :hide-selected="true"
            @choose="onChooseTargets"
            @select-single="addItem($event?.uid)"
          >
            <template #after-options="{ close }">
              <button
                type="button"
                class="options-list__option"
                @click="
                  close();
                  toggleAllOption('target');
                "
              >
                {{ $t("labels.allTargets") }}
              </button>
            </template>
            <template #empty>
              <!-- This list is never empty. -->
              <span></span>
            </template>
          </item-combo-select>
        </template>
      </sortable-list>
    </div>

    <!-- RESOURCES -->
    <div v-if="chartAxisConfig?.item.includes('resource')">
      <h3 class="label mt-0">
        {{ $t("objectNames.resource", 5) }}
      </h3>
      <!-- Marker for ALL resources option -->
      <button-item
        v-if="dataToggles.all.resource"
        class="mb-4"
        :icons="['x']"
        @click:x="toggleAllOption('resource')"
      >
        {{ $t("labels.allResources") }}
      </button-item>

      <sortable-list
        :items="resources"
        :order="selectedResourcesOrder"
        prop="title"
        :sortable="true"
        @update:order="updateItemOrder('resource', $event)"
      >
        <template #default="{ item: resource }">
          <chart-data-button-group
            :chart-uid="chartUid"
            item-type="resource"
            :item-id="resource.id"
            :subgroups="subgroups"
            @remove="removeItem(resource.uid)"
          >
            {{ resource.title }}
          </chart-data-button-group>
        </template>

        <template v-if="!dataToggles.all.resource" #after-list>
          <item-combo-select
            :selection="Object.values(resources)"
            :targets="{}"
            :resources="allAvailableResources"
            :indices="{}"
            :types="['resource']"
            :placeholder="$t('actions.addResource', 2)"
            @choose="onChooseResources"
            @select-single="addItem($event?.uid)"
          >
            <template #after-options="{ close }">
              <button
                type="button"
                class="options-list__option"
                @click="
                  close();
                  toggleAllOption('resource');
                "
              >
                {{ $t("labels.allResources") }}
              </button>
            </template>
            <template #empty>
              <!-- This list is never empty. -->
              <span></span>
            </template>
          </item-combo-select>
        </template>
      </sortable-list>
    </div>

    <!-- INDEX -->
    <div v-if="chartAxisConfig?.item.includes('index') && features.index">
      <h3 class="label mt-0">
        {{ $t("objectNames.index", 5) }}
      </h3>

      <sortable-list
        :items="indices"
        :order="itemOrders.index"
        prop="title"
        :sortable="true"
        @update:order="updateItemOrder('index', $event)"
      >
        <template #default="{ item: index }">
          <chart-data-button
            :chart-uid="chartUid"
            item-type="index"
            :item-id="index.id"
            @remove="removeItem(index.uid)"
          >
            {{ index.title }}
          </chart-data-button>
        </template>
        <template v-if="!dataToggles.all.index" #after-list="{ length }">
          <input-dropdown
            v-if="remainingItemOptions.index.length > 0"
            :options="remainingItemOptions.index"
            :searchable="true"
            :item-key="(i) => `${i.type}_${i.id}`"
            :clear-on-select="true"
            group-key="roundIndex"
            @update:value="addItem($event.uid)"
          >
            <template #default="{ item }">
              {{ item?.title ?? item }}
            </template>
            <template #grouplabel="{ value }">
              <span class="options-list__grouplabel">
                {{ formatSimulationRoundName(parseInt(value)) }}
              </span>
            </template>

            <template #after-options="{ close }">
              <button
                type="button"
                class="options-list__option"
                @click="
                  close();
                  toggleAllOption('index');
                "
              >
                {{ $t("labels.allIndices") }}
              </button>
            </template>
          </input-dropdown>
          <span v-else-if="length == 0" class="message-info">
            {{ $t("errors.noEntries") }}
          </span>
        </template>
      </sortable-list>
    </div>

    <!-- ACTION -->
    <div
      v-if="
        chartAxisConfig?.item.includes('actionActivation') &&
        features.action
      "
    >
      <h3 class="label mt-0">
        {{ $t("objectNames.actionActivation", 5) }}
      </h3>

      <sortable-list
        :items="actionActivations"
        :order="itemOrders.actionActivation"
        prop="title"
        :sortable="true"
        @update:order="updateItemOrder('actionActivation', $event)"
      >
        <template #default="{ item: actionActivation }">
          <chart-data-button
            :chart-uid="chartUid"
            item-type="actionActivation"
            :item-id="actionActivation.id"
            @remove="removeActionActivation(actionActivation.id)"
          >
            {{ actionActivation.title }}
          </chart-data-button>
        </template>
        <template #after-list="{ length }">
          <input-dropdown
            v-if="remainingItemOptions.actionActivation.length > 0"
            :options="remainingItemOptions.actionActivation"
            :searchable="true"
            :item-key="(i) => `${i.type}_${i.id}`"
            :clear-on-select="true"
            group-key="roundIndex"
            @update:value="addActionActivation($event.id)"
          >
            <template #default="{ item }">
              {{ item?.title ?? item }}
            </template>
            <template #grouplabel="{ value }">
              <span class="options-list__grouplabel">
                {{ formatSimulationRoundName(parseInt(value)) }}
              </span>
            </template>
          </input-dropdown>
          <span v-else-if="length == 0" class="message-info">
            {{ $t("errors.noActionsAvailable") }}
          </span>
        </template>
      </sortable-list>
    </div>

    <!-- INCIDENT -->
    <div
      v-if="
        chartAxisConfig?.item.includes('incidentActivation') &&
        features.incident
      "
    >
      <h3 class="label mt-0">
        {{ $t("objectNames.incidentActivation", 5) }}
      </h3>

      <sortable-list
        :items="incidentActivations"
        :order="itemOrders.incidentActivation"
        prop="title"
        :sortable="true"
        @update:order="updateItemOrder('incidentActivation', $event)"
      >
        <template #default="{ item: incidentActivation }">
          <chart-data-button
            :chart-uid="chartUid"
            item-type="incidentActivation"
            :item-id="incidentActivation.id"
            @remove="removeIncidentActivation(incidentActivation.id)"
          >
            {{ incidentActivation.title }}
          </chart-data-button>
        </template>
        <template #after-list="{ length }">
          <input-dropdown
            v-if="remainingItemOptions.incidentActivation.length > 0"
            :options="remainingItemOptions.incidentActivation"
            :searchable="true"
            :item-key="(i) => `${i.type}_${i.id}`"
            :clear-on-select="true"
            group-key="roundIndex"
            @update:value="addIncidentActivation($event.id)"
          >
            <template #default="{ item }">
              {{ item?.title ?? item }}
            </template>
            <template #grouplabel="{ value }">
              <span class="options-list__grouplabel">
                {{ formatSimulationRoundName(parseInt(value)) }}
              </span>
            </template>
          </input-dropdown>
          <span v-else-if="length == 0" class="message-info">
            {{ $t("errors.noEntries") }}
          </span>
        </template>
      </sortable-list>
    </div>

    <!-- SCHEDULED EFFECT -->
    <div
      v-if="
        chartAxisConfig?.item.includes('scheduledEffectActivation') &&
        features.scheduledEffect
      "
    >
      <h3 class="label mt-0">
        {{ $t("objectNames.scheduledEffect", 5) }}
      </h3>

      <sortable-list
        :items="scheduledEffectActivations"
        :order="itemOrders.scheduledEffectActivation"
        prop="title"
        :sortable="true"
        @update:order="updateItemOrder('scheduledEffectActivation', $event)"
      >
        <template #default="{ item: scheduledEffectActivation }">
          <chart-data-button
            :chart-uid="chartUid"
            item-type="scheduledEffectActivation"
            :item-id="scheduledEffectActivation.id"
            @remove="
              removeScheduledEffectActivation(scheduledEffectActivation.id)
            "
          >
            {{ scheduledEffectActivation.title }}
          </chart-data-button>
        </template>
        <template #after-list="{ length }">
          <input-dropdown
            v-if="remainingItemOptions.scheduledEffectActivation.length > 0"
            :options="remainingItemOptions.scheduledEffectActivation"
            :searchable="true"
            :item-key="(i) => `${i.type}_${i.id}`"
            :clear-on-select="true"
            group-key="roundIndex"
            @update:value="addScheduledEffectActivation($event.id)"
          >
            <template #default="{ item }">
              {{ item?.title ?? item }}
            </template>
            <template #grouplabel="{ value }">
              <span class="options-list__grouplabel">
                {{ formatSimulationRoundName(parseInt(value)) }}
              </span>
            </template>
          </input-dropdown>
          <span v-else-if="length == 0" class="message-info">
            {{ $t("errors.noEntries") }}
          </span>
        </template>
      </sortable-list>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { toUid } from '@/helpers/utility/Id';
import { formatRoundName } from "../simulationRound/useRoundTime";

import { useEvaluationStore } from "../evaluation/evaluations.store";
import { useChartData } from "./useChartData";
import { useFeatureStore } from "@editor/store/features.store";
import { useChartStore } from "./charts.store";

import InputDropdown from "@core/input/InputDropdown.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import SortableList from "@core/ui/SortableList.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";

import ChartDataButton from "./ChartBuilderDataButton.vue";
import ChartDataButtonGroup from "./ChartBuilderDataButtonGroup.vue";
import type { SelectableChartItemType } from "./ChartItem";
import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  chartUid: Uid<"evaluation" | "chart">;
}>();

const features = useFeatureStore();

const store = computed(() => {
  if (props.chartUid.startsWith("evaluation")) {
    return useEvaluationStore();
  } 
  else if (props.chartUid.startsWith("chart")) {
    return useChartStore();
  }
  throw new Error("Invalid chart UID");
});

const chart = computed(() => {
  return store.value.byUid[props.chartUid];
});

const {
  targets,
  selectedTargetsOrder,
  resources,
  selectedResourcesOrder,
  indices,
  itemOrders,
  chartAxisConfig,
  dataToggles,
  allAvailableTargets,
  allAvailableResources,
  remainingItemOptions,
  selectedSimulation,

  actionActivations,
  incidentActivations,
  scheduledEffectActivations,
} = useChartData(chart);

const subgroups = computed(() => {
  return chartAxisConfig.value?.subgroups;
});


function toggleAllOption(type: "simulation" | "resource" | "target" | "index") {
  store.value.toggleAllOption(chart.value.id, type);
}

// ========== ADD & REMOVE ITEMS ===========

function addItem(itemUid: Uid<"target" | "resource" | "index"> | undefined) {
  if (!itemUid) return;
  store.value.addItems(chart.value.id, [itemUid]);
}
function removeItem(itemUid: Uid<"target" | "resource" | "index">) {
  store.value.removeItems(chart.value.id, [itemUid]);
}

function updateItemOrder(
  type: SelectableChartItemType,
  order: (string | number)[]
) {
  store.value.updateItemOrder(chart.value.id, type, order);
}

// ========== CHOOSE ELEMENTS ===========

function onChooseTargets(selectedTargets: { uid: Uid<"target">; id: number }[]) {
  const ids = selectedTargets.map((el) => el.id);
  const newIds = ids.filter((id) => !(id in targets.value));
  const idsToRemove = Object.values(targets.value)
    .map((e) => e.id)
    .filter((id) => !ids.includes(id));
  
  console.log("Choosing targets", ids, newIds, idsToRemove);

  store.value.removeItems(chart.value.id, idsToRemove.map((id) => toUid("target", id)));
  store.value.addItems(chart.value.id, newIds.map((id) => toUid("target", id)));
}

function onChooseResources(selectedResources: { id: number }[]) {
  const ids = selectedResources.map((el) => el.id);
  const newIds = ids.filter((id) => !(id in resources.value));
  const idsToRemove = Object.values(resources.value)
    .map((e) => e.id)
    .filter((id) => !ids.includes(id));

  store.value.removeItems(chart.value.id, idsToRemove.map((id) => toUid("resource", id)));
  store.value.addItems(chart.value.id, newIds.map((id) => toUid("resource", id)));
}


// ======== ACTION ACTIVATIONS ===========

function addActionActivation(actionActivationId: string) {
  store.value.addActionActivation(
    chart.value.id,
    actionActivationId
  );
}
function removeActionActivation(actionActivationId: string) {
  store.value.removeActionActivation(
    chart.value.id,
    actionActivationId
  );
}

// ======== INCIDENT ACTIVATIONS ===========

function addIncidentActivation(incidentActivationId: string) {
  store.value.addIncidentActivation(
    chart.value.id,
    incidentActivationId
  );
}
function removeIncidentActivation(incidentActivationId: string) {
  store.value.removeIncidentActivation(
    chart.value.id,
    incidentActivationId
  );
}

// ======== SCHEDULED EFFECT ACTIVATIONS ===========

function addScheduledEffectActivation(activationId: string) {
  store.value.addScheduledEffectActivation(
    chart.value.id,
    activationId
  );
}
function removeScheduledEffectActivation(activationId: string) {
  store.value.removeScheduledEffectActivation(
    chart.value.id,
    activationId
  );
}

// ========== FORMATTING ===========

function formatSimulationRoundName(roundIndex: number) {
  if (selectedSimulation.value) {
    return formatRoundName(
      selectedSimulation.value.durationType,
      selectedSimulation.value.roundUnit,
      selectedSimulation.value.roundLength,
      roundIndex,
      selectedSimulation.value.timeStart
    );
  } else {
    return formatRoundName(
      "roundAmount",
      "round",
      1,
      roundIndex,
      "2020-01-01 00:00:00"
    );
  }
}
</script>